import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import { matchesTurkishSearch } from './utils/turkishSearch';

const SEARCH_ENDPOINT = 'https://tivigo.store/endpoint.php';
const EMPTY_RESULTS = { films: [], series: [], persons: [] };
const RESULTS_BATCH_SIZE = 20;
const INITIAL_VISIBLE_COUNTS = {
  films: RESULTS_BATCH_SIZE,
  series: RESULTS_BATCH_SIZE,
  persons: RESULTS_BATCH_SIZE,
};

const searchFilters = [
  { id: 'films', label: 'Filmler' },
  { id: 'series', label: 'Diziler' },
  { id: 'persons', label: 'Kişiler' },
];

const placeholderScript = [
  { prefix: 'Film arayın: ', text: 'Hızlı ve Öfkeli' },
  { prefix: 'Film arayın: ', text: 'Alacakaranlık' },
  { prefix: 'Film arayın: ', text: 'Inception' },
  { prefix: 'Dizi arayın: ', text: 'La Casa de Papel' },
  { prefix: 'Dizi arayın: ', text: 'Pera Palas' },
  { prefix: 'Dizi arayın: ', text: 'Breaking Bad' },
  { prefix: 'Kişi arayın: ', text: 'Vin Diesel' },
  { prefix: 'Kişi arayın: ', text: 'Kemal Sunal' },
  { prefix: 'Kişi arayın: ', text: 'Leonardo DiCaprio' },
];

const previewResults = {
  films: [
    {
      stream_id: 'preview-inception',
      name: 'Inception',
      stream_icon: '/assets/images/categories/cinema-action.jpg',
      category_name: 'Bilim Kurgu',
      year: '2010',
    },
    {
      stream_id: 'preview-fast',
      name: 'Hızlı ve Öfkeli',
      stream_icon: '/assets/images/categories/cinema-action.jpg',
      category_name: 'Aksiyon',
      year: '2001',
    },
    {
      stream_id: 'preview-twilight',
      name: 'Alacakaranlık',
      stream_icon: '/assets/images/categories/cinema-action.jpg',
      category_name: 'Fantastik',
      year: '2008',
    },
  ],
  series: [
    {
      series_id: 'preview-stranger-things',
      name: 'Stranger Things',
      cover: '/assets/images/showcase/netflix-stranger-things.jpg',
      category_name: 'Bilim Kurgu',
      year: '2016',
    },
    {
      series_id: 'preview-wednesday',
      name: 'Wednesday',
      cover: '/assets/images/showcase/netflix-wednesday.jpg',
      category_name: 'Gizem',
      year: '2022',
    },
    {
      series_id: 'preview-gibi',
      name: 'Gibi',
      cover: '/assets/images/showcase/exxen-gibi.jpg',
      category_name: 'Komedi',
      year: '2021',
    },
  ],
  persons: [
    {
      id: 'preview-vin-diesel',
      type: 'film',
      name: 'Hızlı ve Öfkeli',
      matchedActor: 'Vin Diesel',
      stream_icon: '/assets/images/categories/cinema-action.jpg',
      category_name: 'Aksiyon',
    },
    {
      id: 'preview-kemal-sunal',
      type: 'film',
      name: 'Hababam Sınıfı',
      matchedActor: 'Kemal Sunal',
      stream_icon: '/assets/images/categories/cinema-action.jpg',
      category_name: 'Komedi',
    },
    {
      id: 'preview-feyyaz-yigit',
      type: 'series',
      name: 'Gibi',
      matchedActor: 'Feyyaz Yiğit',
      cover: '/assets/images/showcase/exxen-gibi.jpg',
      category_name: 'Komedi',
    },
  ],
};

const readStoredJson = (key) => {
  if (typeof window === 'undefined') return {};

  try {
    return JSON.parse(window.localStorage.getItem(key) || '{}');
  } catch (error) {
    return {};
  }
};

const getSearchSession = (userInfo) => {
  if (typeof window === 'undefined') {
    return { token: '', server: '', username: '', password: '' };
  }

  const credentials = readStoredJson('xtream_credentials');
  const storedUser = readStoredJson('xtream_user');
  const serverInfo = storedUser.server_info || userInfo?.server_info || {};
  const info = userInfo?.user_info || {};

  return {
    token: window.localStorage.getItem('xtream_api_token') || '',
    server: serverInfo.url || credentials.server || info.server || '',
    username: credentials.username || info.username || '',
    password: credentials.password || info.password || '',
  };
};

const fetchSearchEndpoint = async (endpoint, parameters, signal) => {
  const url = new URL(SEARCH_ENDPOINT);
  url.searchParams.set('endpoint', endpoint);

  Object.entries(parameters).forEach(([key, value]) => {
    url.searchParams.set(key, value == null ? '' : String(value));
  });

  const response = await fetch(url.toString(), { signal });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const payload = await response.json();
  if (!payload?.success) throw new Error(payload?.message || 'Arama isteği başarısız oldu.');

  return Array.isArray(payload.data) ? payload.data : [];
};

const waitForPreview = (signal) => new Promise((resolve) => {
  const timeout = window.setTimeout(() => resolve(true), 220);

  signal.addEventListener('abort', () => {
    window.clearTimeout(timeout);
    resolve(false);
  }, { once: true });
});

function UserPanelSearch({ userInfo, onNavigateToPage, previewMode = false, disabled = false }) {
  const [query, setQuery] = useState('');
  const [placeholder, setPlaceholder] = useState('Film arayın: ');
  const [activeFilter, setActiveFilter] = useState('films');
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [categories, setCategories] = useState({ vod: [], series: [] });
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [visibleCounts, setVisibleCounts] = useState(INITIAL_VISIBLE_COUNTS);
  const searchAnchorRef = useRef(null);
  const searchInputRef = useRef(null);
  const requestControllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const hasSearchedRef = useRef(false);

  useEffect(() => {
    let timeoutId;
    let active = true;
    let scriptIndex = 0;
    let currentText = '';
    let deleting = false;

    const tick = () => {
      if (!active) return;

      const current = placeholderScript[scriptIndex];
      if (!deleting) {
        currentText = current.text.substring(0, currentText.length + 1);
        setPlaceholder(`${current.prefix}${currentText}`);

        if (currentText === current.text) {
          timeoutId = window.setTimeout(() => {
            if (!active) return;
            deleting = true;
            tick();
          }, 1500);
          return;
        }
      } else {
        currentText = current.text.substring(0, currentText.length - 1);
        setPlaceholder(`${current.prefix}${currentText}`);

        if (currentText === '') {
          deleting = false;
          scriptIndex = (scriptIndex + 1) % placeholderScript.length;
          timeoutId = window.setTimeout(tick, 300);
          return;
        }
      }

      timeoutId = window.setTimeout(tick, deleting ? 20 : 40);
    };

    tick();

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (searchExpanded && !disabled) searchInputRef.current?.focus();
  }, [disabled, searchExpanded]);

  useEffect(() => {
    if (!searchExpanded) return undefined;

    const handleSearchOutsideClick = (event) => {
      const target = event.target;
      const clickedSearch = searchAnchorRef.current?.contains(target);
      const clickedResults = target?.closest?.('.tvup-search-popper');

      if (!clickedSearch && !clickedResults) {
        setSearchExpanded(false);
        setSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleSearchOutsideClick);
    document.addEventListener('touchstart', handleSearchOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleSearchOutsideClick);
      document.removeEventListener('touchstart', handleSearchOutsideClick);
    };
  }, [searchExpanded]);

  useEffect(() => {
    const focusSearch = (event) => {
      if (disabled) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase('tr-TR') === 'k') {
        event.preventDefault();
        setSearchExpanded(true);
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', focusSearch);
    return () => window.removeEventListener('keydown', focusSearch);
  }, [disabled]);

  useEffect(() => {
    if (previewMode || disabled) return undefined;

    const session = getSearchSession(userInfo);
    if (!session.token || !session.server || !session.username || !session.password) return undefined;

    const controller = new AbortController();
    const sharedParameters = {
      token: session.token,
      server: session.server,
      username: session.username,
      password: session.password,
    };

    Promise.all([
      fetchSearchEndpoint('get_vod_categories', sharedParameters, controller.signal),
      fetchSearchEndpoint('get_series_categories', sharedParameters, controller.signal),
    ])
      .then(([vod, series]) => setCategories({ vod, series }))
      .catch((error) => {
        if (error.name !== 'AbortError') setCategories({ vod: [], series: [] });
      });

    return () => controller.abort();
  }, [disabled, previewMode, userInfo]);

  useEffect(() => () => {
    requestControllerRef.current?.abort();
  }, []);

  const getCategoryName = useCallback((categoryId, type, fallback) => {
    if (fallback) return fallback;

    const list = type === 'film' ? categories.vod : categories.series;
    const category = list.find((item) => String(item.category_id) === String(categoryId));
    return category?.category_name || 'Kategori bilgisi yok';
  }, [categories]);

  const performSearch = useCallback(async (rawQuery) => {
    if (disabled) return;
    const cleanQuery = rawQuery.trim();

    if (cleanQuery.length < 2) {
      requestControllerRef.current?.abort();
      setResults(EMPTY_RESULTS);
      setSearchError('');
      setSearching(false);
      setSearchOpen(false);
      hasSearchedRef.current = false;
      setVisibleCounts(INITIAL_VISIBLE_COUNTS);
      return;
    }

    requestControllerRef.current?.abort();
    const controller = new AbortController();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    requestControllerRef.current = controller;

    setSearching(true);
    setSearchError('');
    setSearchOpen(true);
    setVisibleCounts(INITIAL_VISIBLE_COUNTS);
    hasSearchedRef.current = true;

    try {
      let nextResults;
      if (previewMode) {
        const shouldContinue = await waitForPreview(controller.signal);
        if (!shouldContinue) return;

        nextResults = {
          films: previewResults.films
            .filter((item) => matchesTurkishSearch(item.name, cleanQuery)),
          series: previewResults.series
            .filter((item) => matchesTurkishSearch(item.name, cleanQuery)),
          persons: previewResults.persons
            .filter((item) => [item.name, item.matchedActor]
              .some((value) => matchesTurkishSearch(value, cleanQuery))),
        };
      } else {
        const session = getSearchSession(userInfo);
        if (!session.token) throw new Error('Arama oturumu bulunamadı.');

        const hasContentCredentials = Boolean(session.server && session.username && session.password);
        const contentParameters = {
          token: session.token,
          server: session.server,
          username: session.username,
          password: session.password,
        };
        const missingCredentialsError = new Error('Yayın hesabı bilgileri bulunamadı.');
        const settledSearches = await Promise.allSettled([
          hasContentCredentials
            ? fetchSearchEndpoint('get_vod_streams', contentParameters, controller.signal)
            : Promise.reject(missingCredentialsError),
          hasContentCredentials
            ? fetchSearchEndpoint('get_series', contentParameters, controller.signal)
            : Promise.reject(missingCredentialsError),
          fetchSearchEndpoint('search_by_cast', {
            token: session.token,
            query: cleanQuery,
          }, controller.signal),
        ]);

        if (settledSearches.every((search) => search.status === 'rejected')) {
          throw settledSearches[0].reason;
        }

        const films = settledSearches[0].status === 'fulfilled' ? settledSearches[0].value : [];
        const series = settledSearches[1].status === 'fulfilled' ? settledSearches[1].value : [];
        const persons = settledSearches[2].status === 'fulfilled' ? settledSearches[2].value : [];

        nextResults = {
          films: films
            .filter((item) => matchesTurkishSearch(item.name, cleanQuery)),
          series: series
            .filter((item) => matchesTurkishSearch(item.name, cleanQuery)),
          persons,
        };
      }

      if (controller.signal.aborted || requestId !== requestIdRef.current) return;

      setResults(nextResults);
      setActiveFilter((currentFilter) => {
        if (nextResults[currentFilter]?.length > 0) return currentFilter;
        return searchFilters.find((filter) => nextResults[filter.id]?.length > 0)?.id || currentFilter;
      });
    } catch (error) {
      if (error.name === 'AbortError' || controller.signal.aborted) return;
      if (requestId !== requestIdRef.current) return;

      setResults(EMPTY_RESULTS);
      setSearchError(error.message || 'Arama sırasında bir bağlantı sorunu oluştu.');
    } finally {
      if (!controller.signal.aborted && requestId === requestIdRef.current) {
        setSearching(false);
      }
    }
  }, [disabled, previewMode, userInfo]);

  const activeResults = useMemo(() => results[activeFilter] || [], [activeFilter, results]);
  const visibleResults = useMemo(
    () => activeResults.slice(0, visibleCounts[activeFilter] || RESULTS_BATCH_SIZE),
    [activeFilter, activeResults, visibleCounts],
  );
  const hasMoreResults = visibleResults.length < activeResults.length;

  const handleQueryChange = (event) => {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    requestControllerRef.current?.abort();
    setResults(EMPTY_RESULTS);
    setSearchError('');
    setSearching(false);
    setSearchOpen(false);
    hasSearchedRef.current = false;
    setVisibleCounts(INITIAL_VISIBLE_COUNTS);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setActiveFilter('films');
    performSearch(query);
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const handleShowMore = () => {
    setVisibleCounts((currentCounts) => ({
      ...currentCounts,
      [activeFilter]: (currentCounts[activeFilter] || RESULTS_BATCH_SIZE) + RESULTS_BATCH_SIZE,
    }));
  };

  const handleResultSelect = (result) => {
    const resultType = activeFilter === 'persons'
      ? result.type
      : activeFilter === 'films' ? 'film' : 'series';
    const isFilm = resultType === 'film';
    const storageKey = isFilm ? 'filmSearchQuery' : 'seriesSearchQuery';
    const eventName = isFilm ? 'filmSearchUpdated' : 'seriesSearchUpdated';
    const destination = isFilm ? 'filmler' : 'diziler';

    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify({
        query: result.name,
        timestamp: Date.now(),
      }));
      window.dispatchEvent(new CustomEvent(eventName));
    } catch (error) {
      // Yönlendirme callback'i storage kullanılamasa da çalışmaya devam eder.
    }

    setSearchOpen(false);
    setSearchExpanded(false);
    setQuery('');
    setResults(EMPTY_RESULTS);
    hasSearchedRef.current = false;
    onNavigateToPage?.(destination, result);
  };

  const getResultKey = (result, index) => (
    result.stream_id || result.series_id || result.id || `${result.type || activeFilter}-${result.name}-${index}`
  );

  const getResultPoster = (result) => (
    activeFilter === 'films' || (activeFilter === 'persons' && result.type === 'film')
      ? result.stream_icon
      : result.cover
  );

  const getResultMeta = (result) => {
    const resultType = activeFilter === 'films' || (activeFilter === 'persons' && result.type === 'film')
      ? 'film'
      : 'series';
    const category = getCategoryName(result.category_id, resultType, result.category_name);
    return [category, result.year, result.matchedActor].filter(Boolean).join(' • ');
  };

  const activeFilterLabel = searchFilters.find((filter) => filter.id === activeFilter)?.label || 'İçerikler';

  return (
    <div className={`tvup-search-wrap${searchExpanded ? ' is-expanded' : ''}`} ref={searchAnchorRef}>
      <form
        className={`tvup-search${searchOpen ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
        role="search"
        onSubmit={handleSubmit}
      >
        <button
          className="tvup-search-toggle"
          type="button"
          aria-label="Aramayı aç"
          aria-expanded={searchExpanded}
          disabled={disabled}
          title={disabled ? 'Hesabınız yeniden aktif olduğunda arama kullanılabilir.' : undefined}
          onClick={() => setSearchExpanded(true)}
        >
          <span className="tvup-search-mark" aria-hidden="true" />
        </button>
        <input
          ref={searchInputRef}
          type="search"
          role="combobox"
          value={query}
          placeholder={placeholder}
          aria-label="Tivigo içeriklerinde ara"
          aria-controls="tvup-search-results"
          aria-expanded={searchOpen}
          aria-haspopup="dialog"
          aria-disabled={disabled || undefined}
          disabled={disabled}
          title={disabled ? 'Hesabınız yeniden aktif olduğunda arama kullanılabilir.' : undefined}
          autoComplete="off"
          spellCheck="false"
          onChange={handleQueryChange}
          onFocus={() => {
            setSearchExpanded(true);
            if (hasSearchedRef.current && query.trim().length >= 2) setSearchOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setSearchOpen(false);
              setSearchExpanded(false);
              event.currentTarget.blur();
            }
          }}
        />

        {query ? (
          <span className="tvup-search-inline-actions">
            <button
              className="tvup-search-submit"
              type="submit"
              disabled={disabled || query.trim().length < 2 || searching}
            >
              {searching ? <span className="tvup-search-spinner is-small" aria-hidden="true" /> : 'Ara'}
            </button>
          </span>
        ) : (
          <kbd>⌘ K</kbd>
        )}
      </form>

      <Popper
        className="tvup-search-popper"
        open={!disabled && searchOpen}
        anchorEl={searchAnchorRef.current}
        placement="bottom-start"
        modifiers={[
          { name: 'offset', options: { offset: [0, 10] } },
          { name: 'preventOverflow', options: { padding: 12 } },
          { name: 'flip', options: { fallbackPlacements: ['bottom-end', 'top-start'] } },
        ]}
      >
        <ClickAwayListener
          onClickAway={(event) => {
            if (!searchAnchorRef.current?.contains(event.target)) setSearchOpen(false);
          }}
        >
          <section
            id="tvup-search-results"
            className="tvup-search-results"
            role="dialog"
            aria-label="Tivigo arama sonuçları"
          >
            <header className="tvup-search-results-head">
              <div>
                <span>AKILLI ARAMA</span>
                <b>Arama sonuçları</b>
              </div>
              <button type="button" aria-label="Arama sonuçlarını kapat" onClick={() => setSearchOpen(false)}>
                <span aria-hidden="true" />
              </button>
            </header>

            <div className="tvup-search-tabs" role="tablist" aria-label="Arama kategorisi">
              {searchFilters.map((filter) => {
                const resultCount = results[filter.id]?.length || 0;

                return (
                  <button
                    key={filter.id}
                    type="button"
                    role="tab"
                    aria-selected={activeFilter === filter.id}
                    className={activeFilter === filter.id ? 'is-active' : ''}
                    onClick={() => handleFilterChange(filter.id)}
                  >
                    {filter.label}
                    {resultCount > 0 && <small>{resultCount} sonuç</small>}
                  </button>
                );
              })}
            </div>

            <div className="tvup-search-results-body" aria-live="polite" aria-busy={searching}>
              {searching && (
                <div className="tvup-search-loading">
                  <span className="tvup-search-spinner" aria-hidden="true" />
                  <b>{activeFilterLabel} içinde aranıyor</b>
                  <small>Sonuçlar hazırlanıyor...</small>
                </div>
              )}

              {!searching && searchError && (
                <div className="tvup-search-state is-error">
                  <span aria-hidden="true">!</span>
                  <b>Arama tamamlanamadı</b>
                  <small>{searchError}</small>
                  <button type="button" onClick={() => performSearch(query)}>Tekrar dene</button>
                </div>
              )}

              {!searching && !searchError && activeResults.length > 0 && (
                <div className="tvup-search-result-list">
                  {visibleResults.map((result, index) => {
                    const poster = getResultPoster(result);
                    const typeLabel = activeFilter === 'persons'
                      ? result.type === 'film' ? 'Film' : 'Dizi'
                      : activeFilter === 'films' ? 'Film' : 'Dizi';

                    return (
                      <button
                        key={getResultKey(result, index)}
                        type="button"
                        className="tvup-search-result"
                        onClick={() => handleResultSelect(result)}
                      >
                        <span className="tvup-search-poster">
                          <span>{String(result.name || '?').charAt(0).toLocaleUpperCase('tr-TR')}</span>
                          {poster && (
                            <img
                              src={poster}
                              alt=""
                              loading="lazy"
                              decoding="async"
                              onError={(event) => event.currentTarget.remove()}
                            />
                          )}
                        </span>
                        <span className="tvup-search-result-copy">
                          <small>{typeLabel}</small>
                          <b>{result.name}</b>
                          <span>{getResultMeta(result)}</span>
                        </span>
                        <span className="tvup-search-result-arrow" aria-hidden="true">↗</span>
                      </button>
                    );
                  })}
                  {hasMoreResults && (
                    <button type="button" className="tvup-search-load-more" onClick={handleShowMore}>
                      {Math.min(RESULTS_BATCH_SIZE, activeResults.length - visibleResults.length)} sonuç daha göster
                    </button>
                  )}
                </div>
              )}

              {!searching && !searchError && activeResults.length === 0 && (
                <div className="tvup-search-state">
                  <span className="tvup-search-state-icon" aria-hidden="true" />
                  <b>Sonuç bulunamadı</b>
                  <small>“{query.trim()}” için {activeFilterLabel.toLocaleLowerCase('tr-TR')} arasında eşleşme yok.</small>
                </div>
              )}
            </div>

            <footer className="tvup-search-results-foot">
              <span><kbd>Enter</kbd> ile ara</span>
              <span>
                {hasMoreResults
                  ? `${visibleResults.length} / ${activeResults.length} sonuç gösteriliyor`
                  : `${activeResults.length} sonuç listelendi`}
              </span>
            </footer>
          </section>
        </ClickAwayListener>
      </Popper>
    </div>
  );
}

export default UserPanelSearch;
